/**
 * DashboardEditorPage - Mirrored Admin Dashboard Editor
 * 
 * A WYSIWYG editor that mirrors the host dashboard, allowing super admins to:
 * - Edit UI elements (titles, text, labels) for each pilot type
 * - Enable/disable features for specific pilot categories
 * - Preview changes before publishing
 * - Changes sync to live HostDashboardV2.jsx
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Settings,
  Eye,
  Save,
  RefreshCw,
  Loader2,
  Users,
  Calendar,
  BarChart3,
  FileText,
  Star,
  MessageSquare,
  Pencil,
  Check,
  X,
  Monitor,
  Smartphone,
  Layout,
  Type,
  Palette,
  Zap,
  AlertCircle,
  ChevronRight,
  ArrowLeft
} from "lucide-react";

// Pilot category configurations
const PILOT_CATEGORIES = [
  { id: "makeup", label: "Makeup", type: "provider" },
  { id: "hair", label: "Hair", type: "provider" },
  { id: "nails", label: "Nails", type: "provider" },
  { id: "lash", label: "Lash", type: "provider" },
  { id: "culinary", label: "Culinary", type: "provider" },
  { id: "wellness", label: "Wellness", type: "provider" },
  { id: "style", label: "Retail", type: "provider" },
  { id: "instructor", label: "Instructor", type: "instructor" },
  { id: "events", label: "Events", type: "events" },
  { id: "hthf", label: "HTHF", type: "instructor" },
  { id: "dispensary", label: "Dispensary", type: "provider" }
];

// Dashboard element configurations
const DASHBOARD_ELEMENTS = {
  header: {
    label: "Header Section",
    icon: Layout,
    fields: [
      { id: "portal_title", label: "Portal Title", type: "text", default: "Host Dashboard" },
      { id: "subtitle", label: "Subtitle", type: "text", default: "Welcome back" },
      { id: "show_stats", label: "Show Stats Bar", type: "toggle", default: true }
    ]
  },
  tabs: {
    label: "Tab Configuration",
    icon: FileText,
    fields: [
      { id: "overview_label", label: "Overview Tab Label", type: "text", default: "Overview" },
      { id: "clients_label", label: "Clients/Participants Tab Label", type: "text", default: "Clients", provider_default: "Clients", event_default: "Participants" },
      { id: "preference_cards_label", label: "Preference Cards Tab Label", type: "text", default: "Preference Cards" },
      { id: "vip_lists_label", label: "VIP Lists Tab Label", type: "text", default: "VIP Lists" },
      { id: "analytics_label", label: "Analytics Tab Label", type: "text", default: "Analytics" },
      { id: "settings_label", label: "Settings Tab Label", type: "text", default: "Settings" }
    ]
  },
  features: {
    label: "Feature Toggles",
    icon: Zap,
    fields: [
      { id: "enable_preference_cards", label: "Enable Preference Cards Tab", type: "toggle", default: true, provider_only: true },
      { id: "enable_vip_lists", label: "Enable VIP Lists Tab", type: "toggle", default: true },
      { id: "enable_analytics", label: "Enable Analytics Tab", type: "toggle", default: true },
      { id: "enable_speakers", label: "Enable Speakers Section", type: "toggle", default: false, event_only: true },
      { id: "enable_quick_actions", label: "Enable Quick Actions", type: "toggle", default: true },
      { id: "enable_notifications", label: "Enable Notifications Panel", type: "toggle", default: true }
    ]
  },
  stats: {
    label: "Stat Cards",
    icon: BarChart3,
    fields: [
      { id: "stat1_label", label: "Stat 1 Label", type: "text", default: "Total Clients", provider_default: "Total Clients", event_default: "Total Attendees" },
      { id: "stat2_label", label: "Stat 2 Label", type: "text", default: "Active Sessions" },
      { id: "stat3_label", label: "Stat 3 Label", type: "text", default: "Revenue" },
      { id: "stat4_label", label: "Stat 4 Label", type: "text", default: "Rating" }
    ]
  },
  quick_actions: {
    label: "Quick Actions",
    icon: Zap,
    fields: [
      { id: "action1_label", label: "Action 1 Label", type: "text", default: "Add Client", provider_default: "Add Client", event_default: "Add Attendee" },
      { id: "action1_enabled", label: "Action 1 Enabled", type: "toggle", default: true },
      { id: "action2_label", label: "Action 2 Label", type: "text", default: "Create Event" },
      { id: "action2_enabled", label: "Action 2 Enabled", type: "toggle", default: true },
      { id: "action3_label", label: "Action 3 Label", type: "text", default: "View Analytics" },
      { id: "action3_enabled", label: "Action 3 Enabled", type: "toggle", default: true }
    ]
  },
  branding: {
    label: "Branding & Colors",
    icon: Palette,
    fields: [
      { id: "primary_color", label: "Primary Color", type: "color", default: "#10b981" },
      { id: "accent_color", label: "Accent Color", type: "color", default: "#8b5cf6" },
      { id: "show_phygital_logo", label: "Show Phygital Logo", type: "toggle", default: true },
      { id: "custom_logo_url", label: "Custom Logo URL", type: "text", default: "" }
    ]
  }
};

export default function DashboardEditorPage() {
  const { pilotCategory } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(pilotCategory || "makeup");
  const [activeSection, setActiveSection] = useState("header");
  const [config, setConfig] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  
  // Fetch existing dashboard config for the selected pilot category
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/dashboard-config/${selectedCategory}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config || getDefaultConfig(selectedCategory));
      } else {
        // Use defaults if no config exists
        setConfig(getDefaultConfig(selectedCategory));
      }
    } catch (err) {
      console.error("Error fetching config:", err);
      setConfig(getDefaultConfig(selectedCategory));
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);
  
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);
  
  // Get default config based on pilot type
  const getDefaultConfig = (category) => {
    const pilot = PILOT_CATEGORIES.find(p => p.id === category);
    const isProvider = pilot?.type === "provider";
    const isEvent = pilot?.type === "events";
    const isInstructor = pilot?.type === "instructor";
    
    const defaultConfig = {};
    
    Object.entries(DASHBOARD_ELEMENTS).forEach(([sectionKey, section]) => {
      defaultConfig[sectionKey] = {};
      section.fields.forEach(field => {
        // Handle conditional defaults
        if (isProvider && field.provider_default !== undefined) {
          defaultConfig[sectionKey][field.id] = field.provider_default;
        } else if (isEvent && field.event_default !== undefined) {
          defaultConfig[sectionKey][field.id] = field.event_default;
        } else {
          defaultConfig[sectionKey][field.id] = field.default;
        }
        
        // Handle feature toggles based on pilot type
        if (field.provider_only && !isProvider) {
          defaultConfig[sectionKey][field.id] = false;
        }
        if (field.event_only && !isEvent) {
          defaultConfig[sectionKey][field.id] = false;
        }
      });
    });
    
    return defaultConfig;
  };
  
  // Update config field
  const updateField = (section, fieldId, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [fieldId]: value
      }
    }));
    setUnsavedChanges(true);
  };
  
  // Save config to backend
  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/dashboard-config/${selectedCategory}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ config })
      });
      
      if (res.ok) {
        toast.success(`Dashboard config saved for ${selectedCategory}!`);
        setUnsavedChanges(false);
      } else {
        toast.error("Failed to save config");
      }
    } catch (err) {
      toast.error("Error saving config");
    } finally {
      setSaving(false);
    }
  };
  
  // Reset to defaults
  const resetToDefaults = () => {
    if (confirm("Reset all settings to defaults? This cannot be undone.")) {
      setConfig(getDefaultConfig(selectedCategory));
      setUnsavedChanges(true);
      toast.info("Reset to defaults - remember to save!");
    }
  };
  
  // Copy config from another pilot
  const copyFromPilot = async (sourcePilot) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/dashboard-config/${sourcePilot}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.config) {
          setConfig(data.config);
          setUnsavedChanges(true);
          toast.success(`Copied config from ${sourcePilot}`);
        }
      }
    } catch (err) {
      toast.error("Failed to copy config");
    }
  };
  
  const currentPilot = PILOT_CATEGORIES.find(p => p.id === selectedCategory);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-editor-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-violet-600" />
                  Dashboard Editor
                </h1>
                <p className="text-sm text-gray-600">
                  Edit host dashboard UI for <Badge className="ml-1 capitalize">{selectedCategory}</Badge>
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {unsavedChanges && (
                <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Unsaved Changes
                </Badge>
              )}
              
              <Button variant="outline" size="sm" onClick={() => setPreviewMode(!previewMode)}>
                <Eye className="w-4 h-4 mr-1" />
                {previewMode ? "Exit Preview" : "Preview"}
              </Button>
              
              <Button variant="outline" size="sm" onClick={resetToDefaults}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Reset
              </Button>
              
              <Button size="sm" onClick={saveConfig} disabled={saving || !unsavedChanges}>
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Pilot Selector & Section Nav */}
          <div className="lg:col-span-1 space-y-4">
            {/* Pilot Category Selector */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Pilot Category
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[200px]">
                  <div className="divide-y">
                    {PILOT_CATEGORIES.map(pilot => (
                      <div
                        key={pilot.id}
                        className={`p-3 cursor-pointer transition-colors ${
                          selectedCategory === pilot.id 
                            ? 'bg-violet-50 border-l-4 border-violet-500' 
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => {
                          if (unsavedChanges && !confirm("You have unsaved changes. Continue?")) return;
                          setSelectedCategory(pilot.id);
                          setUnsavedChanges(false);
                        }}
                        data-testid={`pilot-${pilot.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm capitalize">{pilot.label}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {pilot.type}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
            
            {/* Section Navigator */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Layout className="w-4 h-4" />
                  Dashboard Sections
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {Object.entries(DASHBOARD_ELEMENTS).map(([key, section]) => {
                    const Icon = section.icon;
                    const isActive = activeSection === key;
                    
                    return (
                      <div
                        key={key}
                        className={`p-3 cursor-pointer transition-colors ${
                          isActive 
                            ? 'bg-violet-50 border-l-4 border-violet-500' 
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => setActiveSection(key)}
                        data-testid={`section-${key}`}
                      >
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${isActive ? 'text-violet-600' : 'text-gray-500'}`} />
                          <span className={`text-sm ${isActive ? 'font-medium text-violet-900' : 'text-gray-700'}`}>
                            {section.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            
            {/* Quick Actions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Select onValueChange={(value) => copyFromPilot(value)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Copy from pilot..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PILOT_CATEGORIES.filter(p => p.id !== selectedCategory).map(pilot => (
                      <SelectItem key={pilot.id} value={pilot.id}>
                        Copy from {pilot.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          </div>
          
          {/* Main Content - Editor */}
          <div className="lg:col-span-3">
            {previewMode ? (
              /* Preview Mode */
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Preview - {currentPilot?.label} Dashboard</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant={previewDevice === "desktop" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPreviewDevice("desktop")}
                      >
                        <Monitor className="w-4 h-4" />
                      </Button>
                      <Button
                        variant={previewDevice === "mobile" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPreviewDevice("mobile")}
                      >
                        <Smartphone className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className={`border rounded-lg overflow-hidden ${
                    previewDevice === "mobile" ? "max-w-sm mx-auto" : ""
                  }`}>
                    {/* Mini Dashboard Preview */}
                    <DashboardPreview config={config} pilotType={currentPilot?.type} />
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Editor Mode */
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    {(() => {
                      const SectionIcon = DASHBOARD_ELEMENTS[activeSection]?.icon || Settings;
                      return <SectionIcon className="w-5 h-5 text-violet-600" />;
                    })()}
                    <div>
                      <CardTitle>{DASHBOARD_ELEMENTS[activeSection]?.label}</CardTitle>
                      <CardDescription>
                        Configure {DASHBOARD_ELEMENTS[activeSection]?.label.toLowerCase()} for {currentPilot?.label} dashboards
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {DASHBOARD_ELEMENTS[activeSection]?.fields.map(field => {
                      // Skip fields that don't apply to this pilot type
                      if (field.provider_only && currentPilot?.type !== "provider") return null;
                      if (field.event_only && currentPilot?.type !== "events") return null;
                      
                      const currentValue = config[activeSection]?.[field.id] ?? field.default;
                      
                      return (
                        <div key={field.id} className="space-y-2">
                          <Label className="flex items-center gap-2">
                            {field.label}
                            {(field.provider_only || field.event_only) && (
                              <Badge variant="outline" className="text-[10px]">
                                {field.provider_only ? "Provider only" : "Events only"}
                              </Badge>
                            )}
                          </Label>
                          
                          {field.type === "text" && (
                            <Input
                              value={currentValue || ""}
                              onChange={(e) => updateField(activeSection, field.id, e.target.value)}
                              placeholder={field.default}
                              data-testid={`field-${field.id}`}
                            />
                          )}
                          
                          {field.type === "toggle" && (
                            <div className="flex items-center gap-3">
                              <Switch
                                checked={currentValue}
                                onCheckedChange={(checked) => updateField(activeSection, field.id, checked)}
                                data-testid={`toggle-${field.id}`}
                              />
                              <span className="text-sm text-gray-600">
                                {currentValue ? "Enabled" : "Disabled"}
                              </span>
                            </div>
                          )}
                          
                          {field.type === "color" && (
                            <div className="flex items-center gap-3">
                              <input
                                type="color"
                                value={currentValue || field.default}
                                onChange={(e) => updateField(activeSection, field.id, e.target.value)}
                                className="w-10 h-10 rounded cursor-pointer"
                                data-testid={`color-${field.id}`}
                              />
                              <Input
                                value={currentValue || ""}
                                onChange={(e) => updateField(activeSection, field.id, e.target.value)}
                                placeholder="#10b981"
                                className="flex-1"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Mini Dashboard Preview Component
function DashboardPreview({ config, pilotType }) {
  const headerConfig = config.header || {};
  const tabsConfig = config.tabs || {};
  const statsConfig = config.stats || {};
  const featuresConfig = config.features || {};
  const brandingConfig = config.branding || {};
  
  const primaryColor = brandingConfig.primary_color || "#10b981";
  
  return (
    <div className="bg-gray-900 min-h-[400px] p-4" style={{ "--preview-primary": primaryColor }}>
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-bold text-white">
          {headerConfig.portal_title || "Host Dashboard"}
        </h1>
        <p className="text-gray-400 text-sm">{headerConfig.subtitle || "Welcome back"}</p>
      </div>
      
      {/* Stats Bar */}
      {headerConfig.show_stats !== false && (
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            statsConfig.stat1_label || "Total Clients",
            statsConfig.stat2_label || "Active Sessions",
            statsConfig.stat3_label || "Revenue",
            statsConfig.stat4_label || "Rating"
          ].map((label, idx) => (
            <div key={idx} className="bg-gray-800 rounded p-2 text-center">
              <p className="text-white font-bold">-</p>
              <p className="text-xs text-gray-400">{label}</p>
            </div>
          ))}
        </div>
      )}
      
      {/* Tabs Preview */}
      <div className="flex gap-2 mb-4 overflow-x-auto">
        <div className="px-3 py-1.5 bg-emerald-600 rounded text-white text-xs font-medium">
          {tabsConfig.overview_label || "Overview"}
        </div>
        <div className="px-3 py-1.5 bg-gray-800 rounded text-gray-300 text-xs">
          {tabsConfig.clients_label || "Clients"}
        </div>
        {featuresConfig.enable_preference_cards !== false && pilotType === "provider" && (
          <div className="px-3 py-1.5 bg-gray-800 rounded text-gray-300 text-xs">
            {tabsConfig.preference_cards_label || "Preference Cards"}
          </div>
        )}
        {featuresConfig.enable_vip_lists !== false && (
          <div className="px-3 py-1.5 bg-gray-800 rounded text-gray-300 text-xs">
            {tabsConfig.vip_lists_label || "VIP Lists"}
          </div>
        )}
        {featuresConfig.enable_analytics !== false && (
          <div className="px-3 py-1.5 bg-gray-800 rounded text-gray-300 text-xs">
            {tabsConfig.analytics_label || "Analytics"}
          </div>
        )}
      </div>
      
      {/* Content Placeholder */}
      <div className="bg-gray-800 rounded-lg p-4 min-h-[200px] flex items-center justify-center">
        <p className="text-gray-500 text-sm">Dashboard Content Area</p>
      </div>
    </div>
  );
}
